/**
 * Employer Benefits Page
 *
 * Shows all available benefit programs for the employee including:
 * - Current enrollments
 * - Available programs
 * - Program details and enrollment actions
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Gift,
  Search,
  CheckCircle,
  ArrowRight,
  Heart,
  Brain,
  DollarSign,
  Users,
  Briefcase,
  Shield,
  ChevronDown,
  X,
  Loader2,
  AlertCircle,
  ExternalLink,
  Phone,
  FileText,
  Activity,
  Globe,
  Mail,
  Clock,
} from 'lucide-react';
import { getMemberData, getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import EmployerLayout from '../layout';

interface BenefitProgram {
  id: number;
  program_id?: number;
  program_code: string;
  program_name: string;
  program_type: string;
  program_category: string;
  description: string;
  short_description?: string;
  how_to_access?: string;
  status: string;
  enrollment_status?: string;
  enrollment_id?: number;
  enrolled_at?: string;
  enrollment_progress?: number;
  effective_date?: string;
  end_date?: string;
  vendor_name?: string;
  vendor_website?: string;
  access_url?: string;
  access_phone?: string;
  access_instructions?: string;
  is_free_to_employee?: boolean;
}

interface BenefitDetail {
  id: number;
  program_name: string;
  program_category: string;
  short_description?: string;
  full_description?: string;
  how_to_access?: string;
  eligibility_description?: string;
  vendor: {
    name?: string;
    logo_url?: string;
    website?: string;
    contact_email?: string;
    contact_phone?: string;
  };
  access: {
    url?: string;
    phone?: string;
    instructions?: string;
  };
  cost: {
    is_free_to_employee?: boolean;
    employer_cost?: number;
    employee_cost?: number;
  };
  enrollment?: {
    id: number;
    status: string;
    enrolled_at?: string;
    progress_percentage?: number;
    sessions_count?: number;
    total_minutes_engaged?: number;
    outcome_status?: string;
  };
}

const categoryIcons: Record<string, any> = {
  mental_health: Brain,
  wellness: Heart,
  financial: DollarSign,
  caregiving: Users,
  career: Briefcase,
  insurance: Shield,
  default: Gift,
};

const categoryColors: Record<string, string> = {
  mental_health: 'bg-purple-100 text-purple-700 border-purple-200',
  wellness: 'bg-green-100 text-green-700 border-green-200',
  financial: 'bg-blue-100 text-blue-700 border-blue-200',
  caregiving: 'bg-pink-100 text-pink-700 border-pink-200',
  career: 'bg-orange-100 text-orange-700 border-orange-200',
  insurance: 'bg-gray-100 text-gray-700 border-gray-200',
  default: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

export default function EmployerBenefitsPage() {
  const { t } = useTranslation('employer');
  const [programs, setPrograms] = useState<BenefitProgram[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<BenefitProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showEnrolledOnly, setShowEnrolledOnly] = useState(false);

  // Enrollment modal state
  const [enrollingProgram, setEnrollingProgram] = useState<BenefitProgram | null>(null);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);

  // Detail view state
  const [selectedProgram, setSelectedProgram] = useState<BenefitProgram | null>(null);
  const [programDetail, setProgramDetail] = useState<BenefitDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const member = getMemberData();

  // Handle enrollment
  const handleEnroll = async (program: BenefitProgram) => {
    setEnrollingProgram(program);
    setEnrollmentError(null);
    setEnrollmentSuccess(false);
  };

  const confirmEnrollment = async () => {
    if (!enrollingProgram) return;

    setEnrollmentLoading(true);
    setEnrollmentError(null);

    try {
      const token = getMemberToken();
      if (!token) {
        setEnrollmentError('Not authenticated. Please log in again.');
        return;
      }

      // Use the program_id from the benefit program (linked to the actual program)
      const programId = enrollingProgram.id;
      const enrollUrl = getWeaveinApiUrl(`/api/member-portal/programs/${programId}/enroll`);

      const response = await fetch(enrollUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setEnrollmentSuccess(true);
        // Update local state to show pending (enrollment starts as pending)
        setPrograms(prev => prev.map(p =>
          p.id === enrollingProgram.id
            ? { ...p, enrollment_status: 'PENDING', enrolled_at: new Date().toISOString() }
            : p
        ));
        // Close modal after brief delay
        setTimeout(() => {
          setEnrollingProgram(null);
          setEnrollmentSuccess(false);
        }, 2000);
      } else {
        const errorData = await response.json();
        setEnrollmentError(errorData.detail || 'Failed to enroll. Please try again.');
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      setEnrollmentError('Network error. Please check your connection and try again.');
    } finally {
      setEnrollmentLoading(false);
    }
  };

  // Load program detail
  const loadProgramDetail = async (program: BenefitProgram) => {
    setSelectedProgram(program);
    setDetailLoading(true);
    setProgramDetail(null);

    try {
      const token = getMemberToken();
      if (!token) return;

      const response = await fetch(
        getWeaveinApiUrl(`/api/member-portal/benefits/${program.id}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProgramDetail(data);
      }
    } catch (error) {
      console.error('Error loading program detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const fetchPrograms = async () => {
      if (!member?.id) {
        setLoading(false);
        return;
      }

      try {
        const token = getMemberToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Use the new member-portal/benefits endpoint which includes enrollment status
        const benefitsUrl = getWeaveinApiUrl('/api/member-portal/benefits');
        const response = await fetch(benefitsUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const programsData = await response.json();
          setPrograms(programsData);
          setFilteredPrograms(programsData);
        }
      } catch (error) {
        console.error('Error fetching programs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, [member?.id]);

  // Filter programs based on search and category
  useEffect(() => {
    let filtered = [...programs];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.program_name.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term) ||
          p.program_category?.toLowerCase().includes(term)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.program_category === selectedCategory);
    }

    if (showEnrolledOnly) {
      filtered = filtered.filter((p) =>
        p.enrollment_status === 'ENROLLED' ||
        p.enrollment_status === 'ACTIVE' ||
        p.enrollment_status === 'PENDING'
      );
    }

    setFilteredPrograms(filtered);
  }, [programs, searchTerm, selectedCategory, showEnrolledOnly]);

  const categories = ['all', ...new Set(programs.map((p) => p.program_category).filter(Boolean))];

  const enrolledCount = programs.filter((p) => p.enrollment_status === 'ENROLLED' || p.enrollment_status === 'ACTIVE').length;
  const pendingCount = programs.filter((p) => p.enrollment_status === 'PENDING').length;

  return (
    <EmployerLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('benefits.yourBenefits')}</h1>
            <p className="text-gray-600 mt-1">
              {programs.length} programs available, {enrolledCount} enrolled{pendingCount > 0 ? `, ${pendingCount} pending` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEnrolledOnly(!showEnrolledOnly)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showEnrolledOnly
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CheckCircle className="w-4 h-4 inline-block mr-1" />
              {t('benefits.myEnrollments')}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('benefits.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none px-4 py-2 pr-10 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? t('benefits.allCategories') : cat.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Programs Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse bg-white rounded-xl h-64" />
            ))}
          </div>
        ) : filteredPrograms.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPrograms.map((program) => {
              const IconComponent = categoryIcons[program.program_category] || categoryIcons.default;
              const colorClass = categoryColors[program.program_category] || categoryColors.default;
              const isEnrolled = program.enrollment_status === 'ENROLLED' || program.enrollment_status === 'ACTIVE';
              const isPending = program.enrollment_status === 'PENDING';
              const hasEnrollment = isEnrolled || isPending;

              return (
                <div
                  key={program.id}
                  className={`bg-white rounded-xl shadow-sm border ${
                    isEnrolled
                      ? 'border-green-300 ring-2 ring-green-100'
                      : isPending
                        ? 'border-amber-300 ring-2 ring-amber-100'
                        : 'border-gray-200'
                  } overflow-hidden hover:shadow-md transition-shadow`}
                >
                  {/* Card Header */}
                  <div className={`p-4 ${colorClass.replace('text-', 'bg-').split(' ')[0]}`}>
                    <div className="flex items-start justify-between">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      {isEnrolled && (
                        <span className="px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Enrolled
                        </span>
                      )}
                      {isPending && (
                        <span className="px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded-full flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">{program.program_name}</h3>
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                      {program.description || 'Benefit program available to employees.'}
                    </p>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {program.program_category?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                      {program.vendor_name && (
                        <span className="text-gray-400">by {program.vendor_name}</span>
                      )}
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="px-4 pb-4 space-y-2">
                    {isEnrolled && (
                      <div className="flex items-center gap-2 text-sm text-green-600 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span>Enrolled {program.enrolled_at ? new Date(program.enrolled_at).toLocaleDateString() : ''}</span>
                        {program.enrollment_progress !== undefined && program.enrollment_progress > 0 && (
                          <span className="ml-auto text-gray-500">{program.enrollment_progress}% complete</span>
                        )}
                      </div>
                    )}
                    {isPending && (
                      <div className="flex items-center gap-2 text-sm text-amber-600 mb-2">
                        <Clock className="w-4 h-4" />
                        <span>Pending approval {program.enrolled_at ? `since ${new Date(program.enrolled_at).toLocaleDateString()}` : ''}</span>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadProgramDetail(program)}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isEnrolled
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : isPending
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {t('benefits.viewDetails')}
                      </button>
                      {!hasEnrollment && (
                        <button
                          onClick={() => handleEnroll(program)}
                          className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center"
                        >
                          {t('benefits.enrollNow')} <ArrowRight className="w-4 h-4 ml-1" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Gift className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('benefits.noProgramsFound')}</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || selectedCategory !== 'all'
                ? t('benefits.tryAdjusting')
                : t('benefits.noBenefitsAvailable')}
            </p>
            {(searchTerm || selectedCategory !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                className="text-indigo-600 hover:text-indigo-700 font-medium"
              >
                {t('benefits.clearFilters')}
              </button>
            )}
          </div>
        )}

        {/* Enrollment Confirmation Modal */}
        {enrollingProgram && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {enrollmentSuccess ? t('benefits.enrollmentSubmitted') : t('benefits.confirmEnrollment')}
                </h3>
                <button
                  onClick={() => setEnrollingProgram(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {enrollmentSuccess ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-8 h-8 text-amber-600" />
                    </div>
                    <p className="text-gray-700">
                      Your enrollment request for <strong>{enrollingProgram.program_name}</strong> has been submitted.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Your enrollment is pending approval. You'll be notified once it's approved.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <div className={`p-3 rounded-lg ${categoryColors[enrollingProgram.program_category] || categoryColors.default} inline-flex mb-3`}>
                        {React.createElement(
                          categoryIcons[enrollingProgram.program_category] || categoryIcons.default,
                          { className: 'w-5 h-5' }
                        )}
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{enrollingProgram.program_name}</h4>
                      <p className="text-sm text-gray-600">
                        {enrollingProgram.description || 'Enroll in this benefit program to access its services.'}
                      </p>
                    </div>

                    {enrollmentError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{enrollmentError}</p>
                      </div>
                    )}

                    <p className="text-sm text-gray-500">
                      By clicking &ldquo;{t('benefits.confirmEnrollment')}&rdquo;, you agree to enroll in this program and accept its terms and conditions.
                    </p>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              {!enrollmentSuccess && (
                <div className="flex gap-3 p-4 border-t border-gray-200">
                  <button
                    onClick={() => setEnrollingProgram(null)}
                    disabled={enrollmentLoading}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {t('benefits.cancel') || 'Cancel'}
                  </button>
                  <button
                    onClick={confirmEnrollment}
                    disabled={enrollmentLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                  >
                    {enrollmentLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('benefits.enrolling')}
                      </>
                    ) : (
                      t('benefits.confirmEnrollment')
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Program Detail Modal */}
        {selectedProgram && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
              {/* Detail Modal Header */}
              <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${categoryColors[selectedProgram.program_category] || categoryColors.default}`}>
                    {React.createElement(
                      categoryIcons[selectedProgram.program_category] || categoryIcons.default,
                      { className: 'w-5 h-5' }
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedProgram.program_name}</h3>
                    {selectedProgram.vendor_name && (
                      <p className="text-sm text-gray-500">by {selectedProgram.vendor_name}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedProgram(null);
                    setProgramDetail(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Detail Modal Body */}
              <div className="p-6">
                {detailLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                ) : programDetail ? (
                  <div className="space-y-6">
                    {/* Enrollment Status Banner */}
                    {programDetail.enrollment && (
                      <div className={`rounded-lg p-4 ${
                        programDetail.enrollment.status === 'PENDING'
                          ? 'bg-amber-50 border border-amber-200'
                          : 'bg-green-50 border border-green-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {programDetail.enrollment.status === 'PENDING' ? (
                              <>
                                <Clock className="w-5 h-5 text-amber-600" />
                                <span className="font-medium text-amber-800">{t('benefits.pendingApproval')}</span>
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <span className="font-medium text-green-800">{t('benefits.currentlyEnrolled')}</span>
                              </>
                            )}
                          </div>
                          {programDetail.enrollment.enrolled_at && (
                            <span className={`text-sm ${
                              programDetail.enrollment.status === 'PENDING' ? 'text-amber-600' : 'text-green-600'
                            }`}>
                              Since {new Date(programDetail.enrollment.enrolled_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {programDetail.enrollment.progress_percentage !== undefined && programDetail.enrollment.progress_percentage > 0 && (
                          <div className="mt-3">
                            <div className="flex justify-between text-sm text-green-700 mb-1">
                              <span>Progress</span>
                              <span>{programDetail.enrollment.progress_percentage}%</span>
                            </div>
                            <div className="w-full bg-green-200 rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full transition-all"
                                style={{ width: `${programDetail.enrollment.progress_percentage}%` }}
                              />
                            </div>
                          </div>
                        )}
                        {(programDetail.enrollment.sessions_count || programDetail.enrollment.total_minutes_engaged) && (
                          <div className="mt-3 flex gap-4 text-sm text-green-700">
                            {programDetail.enrollment.sessions_count && (
                              <span><Activity className="w-4 h-4 inline mr-1" />{programDetail.enrollment.sessions_count} sessions</span>
                            )}
                            {programDetail.enrollment.total_minutes_engaged && (
                              <span>{programDetail.enrollment.total_minutes_engaged} minutes</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> {t('benefits.aboutThisBenefit')}
                      </h4>
                      <p className="text-gray-600">
                        {programDetail.full_description || programDetail.short_description || selectedProgram.description}
                      </p>
                    </div>

                    {/* Eligibility */}
                    {programDetail.eligibility_description && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Eligibility</h4>
                        <p className="text-gray-600">{programDetail.eligibility_description}</p>
                      </div>
                    )}

                    {/* How to Access */}
                    {(programDetail.how_to_access || programDetail.access?.instructions) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">{t('benefits.howToAccess')}</h4>
                        <p className="text-blue-800">{programDetail.how_to_access || programDetail.access?.instructions}</p>
                      </div>
                    )}

                    {/* Access Links */}
                    {(programDetail.access?.url || programDetail.access?.phone || programDetail.vendor?.website) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {programDetail.access?.url && (
                          <a
                            href={programDetail.access.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-3 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                          >
                            <ExternalLink className="w-5 h-5" />
                            <span className="font-medium">Access Program</span>
                          </a>
                        )}
                        {programDetail.access?.phone && (
                          <a
                            href={`tel:${programDetail.access.phone}`}
                            className="flex items-center gap-2 p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <Phone className="w-5 h-5" />
                            <span className="font-medium">{programDetail.access.phone}</span>
                          </a>
                        )}
                        {programDetail.vendor?.website && (
                          <a
                            href={programDetail.vendor.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <Globe className="w-5 h-5" />
                            <span className="font-medium">Vendor Website</span>
                          </a>
                        )}
                        {programDetail.vendor?.contact_email && (
                          <a
                            href={`mailto:${programDetail.vendor.contact_email}`}
                            className="flex items-center gap-2 p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <Mail className="w-5 h-5" />
                            <span className="font-medium">Contact Vendor</span>
                          </a>
                        )}
                      </div>
                    )}

                    {/* Cost Information */}
                    {programDetail.cost && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{t('benefits.cost')}</h4>
                        {programDetail.cost.is_free_to_employee ? (
                          <p className="text-green-600 font-medium">Free to you - covered by your employer</p>
                        ) : programDetail.cost.employee_cost ? (
                          <p className="text-gray-700">Your cost: ${programDetail.cost.employee_cost.toFixed(2)}</p>
                        ) : (
                          <p className="text-gray-500">Contact HR for cost details</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-gray-600">{selectedProgram.description}</p>
                    {selectedProgram.how_to_access && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">{t('benefits.howToAccess')}</h4>
                        <p className="text-blue-800">{selectedProgram.how_to_access}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Detail Modal Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
                <button
                  onClick={() => {
                    setSelectedProgram(null);
                    setProgramDetail(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  {t('benefits.close') || 'Close'}
                </button>
                {!selectedProgram.enrollment_status ||
                  (selectedProgram.enrollment_status !== 'ENROLLED' &&
                   selectedProgram.enrollment_status !== 'ACTIVE' &&
                   selectedProgram.enrollment_status !== 'PENDING') ? (
                  <button
                    onClick={() => {
                      setSelectedProgram(null);
                      setProgramDetail(null);
                      handleEnroll(selectedProgram);
                    }}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center"
                  >
                    {t('benefits.enrollNow')} <ArrowRight className="w-4 h-4 ml-1" />
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </EmployerLayout>
  );
}
